import {
  createContext,
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createHttpClient } from '@shared/api/httpClient';
import { getRuntimeConfig } from '@shared/config/runtime';
import type { AiChatRequest, AiChatResponse } from '@shared/types/api';
import { useClientAccount } from '../account/AccountContext';

type ClientAiPageContext = {
  pageType?: string;
  packageId?: string;
  packageName?: string;
  packageType?: string;
  controlId?: string;
  controlTitle?: string;
  documentId?: string;
  documentTitle?: string;
  sourceLabel?: string;
};

type ClientAiScope = {
  packageId?: string;
  packageName?: string;
  packageType?: string;
  controlId?: string;
  controlTitle?: string;
  documentId?: string;
  documentTitle?: string;
  sourceLabel?: string;
  pageType?: string;
  selectedPackageId?: string;
  selectedControlId?: string;
  selectedDocumentId?: string;
  pageContext?: ClientAiPageContext;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

type ClientAiOpenOptions = {
  autoSubmit?: boolean;
  displayMessage?: string;
};

type ClientAiAssistantContextValue = {
  scope: ClientAiScope;
  setPageScope: (scope: ClientAiScope) => void;
  clearPageScope: () => void;
  openDrawer: (scope?: ClientAiScope, draft?: string, options?: ClientAiOpenOptions) => void;
  closeDrawer: () => void;
  resetDrawerScope: () => void;
};

const ClientAiAssistantContext = createContext<ClientAiAssistantContextValue | null>(null);

const GENERAL_PROMPTS = [
  'What should I work on next?',
  'What does my current readiness score mean?',
  'Which evidence items need attention?',
  'Am I ready for assessor review?',
];

const PACKAGE_PROMPTS = [
  'Summarize this package in plain language.',
  'What should I work on next for this package?',
  'Which controls still need evidence?',
  'What should I confirm before assessor review?',
];

const CONTROL_PROMPTS = [
  'Explain this control',
  'Evidence needed',
  'Why incomplete?',
  'Next step',
];

const DOCUMENT_PROMPTS = [
  'What is this?',
  'How do I complete it?',
  'Who reviews/signs?',
  'What should I confirm?',
];

function scopeSignature(scope: ClientAiScope): string {
  return [
    scope.pageType,
    scope.packageId,
    scope.selectedPackageId,
    scope.packageType,
    scope.controlId,
    scope.selectedControlId,
    scope.documentId,
    scope.selectedDocumentId,
    scope.pageContext?.pageType,
    scope.pageContext?.packageId,
  ]
    .map((value) => String(value ?? '').trim())
    .join('|');
}

function mergeScopes(base: ClientAiScope, overlay?: ClientAiScope): ClientAiScope {
  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(overlay ?? {}).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== ''),
    ),
  };
}

function normalizeScope(scope: ClientAiScope): ClientAiScope {
  const pageContext = scope.pageContext ?? {};
  const packageId = String(scope.packageId || scope.selectedPackageId || pageContext.packageId || '').trim() || undefined;
  const controlId = String(scope.controlId || scope.selectedControlId || pageContext.controlId || '').trim() || undefined;
  const documentId = String(scope.documentId || scope.selectedDocumentId || pageContext.documentId || '').trim() || undefined;
  const pageType = String(scope.pageType || pageContext.pageType || '').trim() || undefined;

  return {
    ...scope,
    packageId,
    controlId,
    documentId,
    selectedPackageId: String(scope.selectedPackageId || packageId || '').trim() || undefined,
    selectedControlId: String(scope.selectedControlId || controlId || '').trim() || undefined,
    selectedDocumentId: String(scope.selectedDocumentId || documentId || '').trim() || undefined,
    pageType,
    pageContext: {
      ...pageContext,
      pageType,
      packageId,
      packageName: scope.packageName || pageContext.packageName,
      packageType: scope.packageType || pageContext.packageType,
      controlId,
      controlTitle: scope.controlTitle || pageContext.controlTitle,
      documentId,
      documentTitle: scope.documentTitle || pageContext.documentTitle,
      sourceLabel: scope.sourceLabel || pageContext.sourceLabel,
    },
  };
}

function explicitScope(scope?: ClientAiScope): ClientAiScope | undefined {
  if (!scope) return undefined;
  return normalizeScope(scope);
}

function compactIdentifier(value?: string, maxLength = 42): string {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(8, maxLength - 12))}…${text.slice(-8)}`;
}

function friendlyScopeValue(value?: string): string {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function scopeTitle(scope: ClientAiScope): string {
  if (scope.documentId) return scope.documentTitle ? compactIdentifier(scope.documentTitle, 46) : 'Evidence document';
  if (scope.controlId) return scope.controlId;
  if (scope.packageId) return compactIdentifier(scope.packageName || scope.packageId, 46) || 'Package guidance';
  return 'Readiness guidance';
}

function promptChips(scope: ClientAiScope): string[] {
  if (scope.documentId) return DOCUMENT_PROMPTS;
  if (scope.controlId) return CONTROL_PROMPTS;
  if (scope.packageId) return PACKAGE_PROMPTS;
  return GENERAL_PROMPTS;
}

function promptTextForChip(prompt: string, scope: ClientAiScope): string {
  if (scope.controlId) {
    const controlLabel = scope.controlTitle ? `${scope.controlId} (${scope.controlTitle})` : scope.controlId;
    if (prompt === 'Explain this control') {
      return `What is CMMC control ${controlLabel}? This question came from the AI shortcut for one selected control. Answer specifically about this control, not with a generic package summary. Explain what the control requires in plain language, what risk it reduces, why it matters for CMMC readiness, what the current package status means if known, what evidence categories should be reviewed, and the next client-safe step. Do not perform any portal actions.`;
    }
    if (prompt === 'Evidence needed') {
      return `What evidence is needed for CMMC control ${controlLabel}? This question came from the AI shortcut for one selected control. Answer specifically about this control. Include practical evidence categories, how the evidence supports the control, and the next client-safe step. Do not perform any portal actions.`;
    }
    if (prompt === 'Why incomplete?') {
      return `Why is CMMC control ${controlLabel} not complete yet? This question came from the AI shortcut for one selected control. Use the current package status and reason/action if available. Explain the gap in client-friendly language and what evidence should be reviewed next. Do not perform any portal actions.`;
    }
    if (prompt === 'Next step') {
      return `What should I do next for CMMC control ${controlLabel}? This question came from the AI shortcut for one selected control. Give a practical, client-safe next step that helps move the control toward readiness without performing any portal action. Do not perform any portal actions.`;
    }
  }

  if (scope.documentId) {
    const documentName = scope.documentTitle || scope.documentId || 'this Evidence Workspace document';
    const controlContext = scope.controlId
      ? ` It appears related to control ${scope.controlTitle ? `${scope.controlId} (${scope.controlTitle})` : scope.controlId}.`
      : '';
    if (prompt === 'What is this?') {
      return `What is ${documentName}?${controlContext} This question came from the AI shortcut for one selected Evidence Workspace document. Answer specifically about this document, not with generic instructions for all prepared evidence documents. Explain what it is likely used for in a CMMC readiness package, why it matters, who should review or complete it, what customer-specific information should be confirmed, what supporting evidence may be needed, and how to complete it correctly. Do not perform any portal actions.`;
    }
    if (prompt === 'How do I complete it?') {
      return `How do I complete ${documentName} correctly?${controlContext} This question came from the AI shortcut for one selected Evidence Workspace document. Answer specifically about this document. Explain the likely purpose of the document, who should review or sign it if applicable, what information the customer should verify before completing it, what evidence or records may support it, and how the customer should complete the matching Evidence Workspace row. Do not perform any portal actions.`;
    }
    if (prompt === 'Who reviews/signs?') {
      return `Who should review or sign ${documentName}?${controlContext} This question came from the AI shortcut for one selected Evidence Workspace document. Explain likely responsible roles, when leadership or compliance review may be needed, what should be verified before signing, and how to keep the answer practical and client-safe. Do not perform any portal actions.`;
    }
    if (prompt === 'What should I confirm?') {
      return `What should I confirm before completing ${documentName}?${controlContext} This question came from the AI shortcut for one selected Evidence Workspace document. Explain the customer-specific facts, evidence, dates, approvals, signatures, and supporting records that may need to be checked before the customer updates the matching Evidence Workspace row. Do not perform any portal actions.`;
    }
  }

  return prompt;
}

function firstErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'The AI Assistant request failed.';
}

function cleanAssistantAnswer(content: string): string {
  const normalized = String(content || '').replace(/\r\n/g, '\n').trim();
  if (!normalized) return '';

  const lines = normalized.split('\n');
  const blockedHeadings = new Set([
    'basis',
    'traceability',
    'sources',
    'source references',
    'tool trace',
    'raw sources',
    'metadata',
  ]);
  const kept: string[] = [];
  let skipBlock = false;

  for (const rawLine of lines) {
    const heading = rawLine
      .trim()
      .replace(/^#{1,6}\s*/, '')
      .replace(/:$/, '')
      .trim()
      .toLowerCase();

    if (blockedHeadings.has(heading)) {
      skipBlock = true;
      continue;
    }
    if (skipBlock && /^#{1,6}\s+/.test(rawLine)) {
      skipBlock = false;
    }
    if (!skipBlock) kept.push(rawLine);
  }

  return kept.join('\n').trim();
}

function renderInlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;
    if (token.startsWith('**')) {
      parts.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={key}>{token.slice(1, -1)}</code>);
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function stripListMarker(line: string): string {
  return line.replace(/^\s*(?:[-*•]|\d+\.)\s+/, '').trim();
}

type OrderedListItem = {
  ordinal: number;
  text: string;
};

function parseOrderedListItem(line: string): OrderedListItem | null {
  const match = /^\s*(\d+)\.\s+(.+)$/.exec(line);
  if (!match) return null;

  const ordinal = Number.parseInt(match[1], 10);
  if (!Number.isFinite(ordinal) || ordinal < 1) return null;

  return { ordinal, text: match[2].trim() };
}

function formatAssistantAnswer(content: string): ReactNode[] {
  const cleaned = cleanAssistantAnswer(content);
  if (!cleaned) return [<p key="empty">No answer was returned.</p>];

  const lines = cleaned.split('\n');
  const blocks: ReactNode[] = [];
  let index = 0;
  let paragraph: string[] = [];

  function flushParagraph() {
    const text = paragraph.join(' ').replace(/\s+/g, ' ').trim();
    if (text) {
      blocks.push(<p key={`p-${blocks.length}`}>{renderInlineMarkdown(text, `p-${blocks.length}`)}</p>);
    }
    paragraph = [];
  }

  while (index < lines.length) {
    const rawLine = lines[index];
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      index += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      const headingText = heading[2].trim();
      const headingKey = `h-${blocks.length}`;
      blocks.push(<h3 key={headingKey}>{renderInlineMarkdown(headingText, headingKey)}</h3>);
      index += 1;
      continue;
    }

    if (/^\s*[-*•]\s+/.test(rawLine)) {
      flushParagraph();
      const items: string[] = [];
      while (index < lines.length) {
        if (/^\s*[-*•]\s+/.test(lines[index])) {
          items.push(stripListMarker(lines[index]));
          index += 1;
          continue;
        }

        if (
          !lines[index].trim() &&
          index + 1 < lines.length &&
          /^\s*[-*•]\s+/.test(lines[index + 1])
        ) {
          index += 1;
          continue;
        }

        break;
      }
      const listKey = `ul-${blocks.length}`;
      blocks.push(
        <ul key={listKey}>
          {items.map((item, itemIndex) => (
            <li key={`${listKey}-${itemIndex}`}>{renderInlineMarkdown(item, `${listKey}-${itemIndex}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\s*\d+\.\s+/.test(rawLine)) {
      flushParagraph();
      const items: OrderedListItem[] = [];
      while (index < lines.length) {
        const item = parseOrderedListItem(lines[index]);
        if (item) {
          items.push(item);
          index += 1;
          continue;
        }

        if (
          !lines[index].trim() &&
          index + 1 < lines.length &&
          parseOrderedListItem(lines[index + 1])
        ) {
          index += 1;
          continue;
        }

        break;
      }
      const listKey = `ol-${blocks.length}`;
      blocks.push(
        <ol key={listKey} start={items[0]?.ordinal}>
          {items.map((item, itemIndex) => (
            <li key={`${listKey}-${itemIndex}`} value={item.ordinal}>
              {renderInlineMarkdown(item.text, `${listKey}-${itemIndex}`)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    paragraph.push(line);
    index += 1;
  }

  flushParagraph();
  return blocks;
}

function FormattedAiMessage({ content }: { content: string }) {
  return <div className="client-ai-formatted-answer">{formatAssistantAnswer(content)}</div>;
}

export function ClientAiAssistantProvider({ children }: PropsWithChildren) {
  const [pageScope, setPageScopeState] = useState<ClientAiScope>({});
  const [manualScope, setManualScope] = useState<ClientAiScope>({});
  const [forceGeneralScope, setForceGeneralScope] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState('');
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);
  const [pendingDisplayMessage, setPendingDisplayMessage] = useState('');

  const activeScope = useMemo(() => (forceGeneralScope ? {} : normalizeScope(mergeScopes(pageScope, manualScope))), [forceGeneralScope, manualScope, pageScope]);

  const setPageScope = useCallback((scope: ClientAiScope) => {
    const nextScope = normalizeScope(scope);
    setPageScopeState(nextScope);
    setForceGeneralScope(false);
    setManualScope((current) => {
      const currentScope = normalizeScope(current);
      const currentPackageId = currentScope.packageId || currentScope.selectedPackageId;
      const nextPackageId = nextScope.packageId || nextScope.selectedPackageId;
      if (currentPackageId && nextPackageId && currentPackageId !== nextPackageId) {
        return {};
      }
      return current;
    });
  }, []);

  const clearPageScope = useCallback(() => {
    setPageScopeState({});
  }, []);

  const openDrawer = useCallback((scope?: ClientAiScope, draft?: string, options?: ClientAiOpenOptions) => {
    const nextScope = explicitScope(scope);
    if (nextScope) {
      setForceGeneralScope(false);
      // Explicit page/button actions replace manual scope so stale control/document/package
      // context cannot bleed into a new package-level request.
      setManualScope(nextScope);
    }
    if (draft) {
      setPendingDraft(draft);
      setPendingAutoSubmit(Boolean(options?.autoSubmit));
      setPendingDisplayMessage(String(options?.displayMessage || '').trim());
    } else {
      setPendingAutoSubmit(false);
      setPendingDisplayMessage('');
    }
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => setIsOpen(false), []);

  const resetDrawerScope = useCallback(() => {
    setManualScope({});
    // Starting a new conversation should clear only the manually selected
    // control/document/package override. Keep the current page scope so the
    // assistant continues to use the package shown on the dashboard or detail
    // page instead of sending an empty pageContext.
    setForceGeneralScope(false);
  }, []);

  return (
    <ClientAiAssistantContext.Provider value={{ scope: activeScope, setPageScope, clearPageScope, openDrawer, closeDrawer, resetDrawerScope }}>
      {children}
      <ClientAiAssistantDrawer
        scope={activeScope}
        isOpen={isOpen}
        pendingDraft={pendingDraft}
        pendingAutoSubmit={pendingAutoSubmit}
        pendingDisplayMessage={pendingDisplayMessage}
        clearPendingDraft={() => {
          setPendingDraft('');
          setPendingAutoSubmit(false);
          setPendingDisplayMessage('');
        }}
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
        resetDrawerScope={resetDrawerScope}
      />
    </ClientAiAssistantContext.Provider>
  );
}

export function useClientAiAssistant() {
  const value = useContext(ClientAiAssistantContext);
  if (!value) throw new Error('useClientAiAssistant must be used within ClientAiAssistantProvider.');
  return value;
}

export function useClientAiAssistantPageScope(scope: ClientAiScope) {
  const { setPageScope, clearPageScope } = useClientAiAssistant();
  const signature = scopeSignature(scope);

  useEffect(() => {
    setPageScope(scope);
    return () => clearPageScope();
    // signature intentionally controls equivalent scope updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, setPageScope, clearPageScope]);
}

function ClientAiAssistantDrawer({
  scope,
  isOpen,
  pendingDraft,
  pendingAutoSubmit,
  pendingDisplayMessage,
  clearPendingDraft,
  openDrawer,
  closeDrawer,
  resetDrawerScope,
}: {
  scope: ClientAiScope;
  isOpen: boolean;
  pendingDraft: string;
  pendingAutoSubmit: boolean;
  pendingDisplayMessage: string;
  clearPendingDraft: () => void;
  openDrawer: (scope?: ClientAiScope, draft?: string, options?: ClientAiOpenOptions) => void;
  closeDrawer: () => void;
  resetDrawerScope: () => void;
}) {
  const config = getRuntimeConfig();
  const enabled = config.aiAssistantClientPortalEnabled === true;
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const { selectedAccount } = useClientAccount();
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastScopeSignature = useRef('');
  const sessionScopeSignatureRef = useRef('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollTargetMessageRef = useRef<HTMLElement | null>(null);
  const [scrollTargetMessageId, setScrollTargetMessageId] = useState<string | undefined>();
  const activeSignature = scopeSignature(scope);

  const canSend = draft.trim().length > 0 && !loading;
  const composerPlaceholder = scope.packageId
    ? 'Ask about this package, control, document, or next step…'
    : 'Ask about readiness, evidence, or assessor preparation…';

  function focusComposer() {
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  }

  async function sendDraftMessage(messageText: string, displayText?: string) {
    const message = messageText.trim();
    if (!message || loading) return;

    const normalizedScope = normalizeScope(scope);
    const requestSessionId = sessionScopeSignatureRef.current === activeSignature ? sessionId : undefined;
    const request: AiChatRequest & Record<string, unknown> = {
      accountId: selectedAccount?.accountId,
      message,
      sessionId: requestSessionId,
      packageId: normalizedScope.packageId,
      packageType: normalizedScope.packageType,
      controlId: normalizedScope.controlId,
      documentId: normalizedScope.documentId,
      selectedPackageId: normalizedScope.selectedPackageId || normalizedScope.packageId,
      selectedControlId: normalizedScope.selectedControlId || normalizedScope.controlId,
      selectedDocumentId: normalizedScope.selectedDocumentId || normalizedScope.documentId,
      pagePackageId: normalizedScope.pageContext?.packageId || normalizedScope.packageId,
      pageType: normalizedScope.pageContext?.pageType || normalizedScope.pageType,
      sourceLabel: normalizedScope.sourceLabel || normalizedScope.pageContext?.sourceLabel,
      pageContext: {
        pageType: normalizedScope.pageContext?.pageType || normalizedScope.pageType,
        packageId: normalizedScope.pageContext?.packageId || normalizedScope.packageId,
        packageName: normalizedScope.pageContext?.packageName || normalizedScope.packageName,
        packageType: normalizedScope.pageContext?.packageType || normalizedScope.packageType,
        controlId: normalizedScope.pageContext?.controlId || normalizedScope.controlId,
        controlTitle: normalizedScope.pageContext?.controlTitle || normalizedScope.controlTitle,
        documentId: normalizedScope.pageContext?.documentId || normalizedScope.documentId,
        documentTitle: normalizedScope.pageContext?.documentTitle || normalizedScope.documentTitle,
        sourceLabel: normalizedScope.pageContext?.sourceLabel || normalizedScope.sourceLabel,
      },
    };

    const userMessage: ChatMessage = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: String(displayText || message).trim(),
    };

    setMessages((current) => [...current, userMessage]);
    setScrollTargetMessageId(userMessage.id);
    setDraft('');
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<AiChatResponse>('/ai/chat', request);
      setSessionId(response.sessionId);
      sessionScopeSignatureRef.current = activeSignature;
      setMessages((current) => [
        ...current,
        {
          id: response.messageId || `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.answer || 'No answer was returned.',
        },
      ]);
    } catch (requestError) {
      setError(firstErrorMessage(requestError));
    } finally {
      setLoading(false);
      focusComposer();
    }
  }

  useEffect(() => {
    if (lastScopeSignature.current && lastScopeSignature.current !== activeSignature) {
      setSessionId(undefined);
      sessionScopeSignatureRef.current = activeSignature;
      setMessages([]);
      setScrollTargetMessageId(undefined);
      setError(null);
    }
    if (!sessionScopeSignatureRef.current) {
      sessionScopeSignatureRef.current = activeSignature;
    }
    lastScopeSignature.current = activeSignature;
  }, [activeSignature]);

  useEffect(() => {
    if (!scrollTargetMessageId) return;
    const frameId = window.requestAnimationFrame(() => {
      scrollTargetMessageRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      setScrollTargetMessageId(undefined);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [messages, scrollTargetMessageId]);

  useEffect(() => {
    if (!pendingDraft) return;

    const nextDraft = pendingDraft;
    const displayText = pendingDisplayMessage || pendingDraft;
    setDraft(nextDraft);
    clearPendingDraft();

    if (pendingAutoSubmit) {
      window.setTimeout(() => {
        void sendDraftMessage(nextDraft, displayText);
      }, 0);
    }
  // sendDraftMessage intentionally omitted to avoid resubmitting when session state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDraft, pendingAutoSubmit, pendingDisplayMessage, clearPendingDraft]);

  function usePrompt(prompt: string) {
    const promptText = promptTextForChip(prompt, scope);
    setDraft(promptText);
    if (!loading) {
      void sendDraftMessage(promptText, prompt);
    } else {
      focusComposer();
    }
  }

  async function sendMessage(event?: FormEvent) {
    event?.preventDefault();
    if (!canSend) return;
    await sendDraftMessage(draft);
  }

  function handleComposerKeyDown(event: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canSend) void sendMessage();
  }

  function startNewChat() {
    setSessionId(undefined);
    sessionScopeSignatureRef.current = activeSignature;
    setMessages([]);
    setScrollTargetMessageId(undefined);
    setDraft('');
    setError(null);
    resetDrawerScope();
  }

  if (!enabled) return null;

  return (
    <>
      <button
        className="client-ai-launcher"
        type="button"
        onClick={() => openDrawer()}
        aria-label="Open SURELINC Readiness AI Assistant™"
        title="Open SURELINC Readiness AI Assistant™"
      >
        <span aria-hidden="true">✦</span>
        <span>SURELINC AI</span>
      </button>

      {isOpen && <div className="client-ai-backdrop" onClick={closeDrawer} aria-hidden="true" />}

      <aside className={isOpen ? 'client-ai-drawer client-ai-drawer-open' : 'client-ai-drawer'} aria-label="SURELINC Readiness AI Assistant™" aria-hidden={!isOpen}>
        <header className="client-ai-header">
          <div>
            <div className="client-ai-kicker">SURELINC Readiness AI Assistant™</div>
            <h2>{scopeTitle(scope)}</h2>
          </div>
          <div className="client-ai-header-actions">
            <button className="secondary-button" type="button" onClick={startNewChat}>New</button>
            <button className="client-ai-icon-button" type="button" onClick={closeDrawer} aria-label="Close SURELINC Readiness AI Assistant">×</button>
          </div>
        </header>


        <section className="client-ai-prompts" aria-label="Suggested AI prompts">
          {promptChips(scope).map((prompt) => (
            <button key={prompt} type="button" onClick={() => usePrompt(prompt)} disabled={loading}>{prompt}</button>
          ))}
        </section>

        <section className="client-ai-messages" aria-live="polite">
          {messages.length === 0 && (
            <div className="client-ai-empty-state">
              <h3>Ask a direct question</h3>
              <p>Answers use your SURELINC Readiness Platform™ records.</p>
            </div>
          )}

          {messages.map((message) => (
            <article
              key={message.id}
              ref={message.id === scrollTargetMessageId ? scrollTargetMessageRef : undefined}
              className={`client-ai-message client-ai-message-${message.role}`}
            >
              <div className="client-ai-message-role">{message.role === 'user' ? 'You' : 'SURELINC Readiness AI Assistant™'}</div>
              <div className="client-ai-message-content">
                {message.role === 'assistant' ? <FormattedAiMessage content={message.content} /> : message.content}
              </div>
            </article>
          ))}

          {loading && <div className="client-ai-loading">Reviewing your readiness records…</div>}
          {error && <div className="client-ai-notice client-ai-notice-error">{error}</div>}
        </section>

        <form className="client-ai-composer" onSubmit={sendMessage}>
          <label className="client-ai-composer-label" htmlFor="client-ai-direct-question">Ask a direct question</label>
          <textarea
            id="client-ai-direct-question"
            ref={textareaRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            placeholder={composerPlaceholder}
            rows={3}
            maxLength={2000}
            disabled={loading}
          />
          <div className="client-ai-composer-footer">
            <span>Enter to send · Shift+Enter for a new line · {draft.length}/2000</span>
            <button className="primary-button" type="submit" disabled={!canSend}>{loading ? 'Sending…' : 'Send'}</button>
          </div>
          <p className="client-ai-caution">AI-generated guidance can make mistakes. Verify important details against your package records and assessor requirements.</p>
        </form>
      </aside>
    </>
  );
}
